import { useEffect, useState } from "react";
import api, { money, shortDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Button, Input, Select, Modal, PageHeader, EmptyState, Badge } from "@/components/kit";
import { HandCoins, CheckCircle, XCircle, Plus } from "@phosphor-icons/react";

const reqTypeLabel = (t) => ({ deposit: "Against deposit", general: "General loan", commodity: "Commodity loan" }[t] || t || "Loan");

export default function LoanRequestsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("requested");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pick, setPick] = useState(null);
  const [decision, setDecision] = useState("approved");
  const [form, setForm] = useState({ interest_rate: "", interest_method: "SI", file_charge_type: "", file_charge_value: "", note: "" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [raiseOpen, setRaiseOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/loan-requests");
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const canDecide = ["super_admin", "branch_admin"].includes(user.role);
  const canRaise = ["agent", "super_admin", "branch_admin", "staff"].includes(user.role);
  const buckets = {
    requested: rows.filter((r) => r.status === "requested"),
    approved: rows.filter((r) => ["approved", "dispatched"].includes(r.status)),
    rejected: rows.filter((r) => r.status === "rejected"),
  };
  const list = buckets[tab] || [];

  const openDecide = (r, d) => {
    setPick(r); setDecision(d);
    setForm({
      interest_rate: r.interest_rate != null ? String(r.interest_rate) : "",
      interest_method: r.interest_method || "SI",
      file_charge_type: "", file_charge_value: "", note: "",
    });
    setErr("");
  };

  const submitDecision = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      const body = { decision };
      if (decision === "approved") {
        if (!form.interest_rate) { setErr("Interest rate is required to approve"); setSaving(false); return; }
        body.interest_rate = Number(form.interest_rate);
        body.interest_method = form.interest_method || "SI";
        if (form.file_charge_type) body.file_charge_type = form.file_charge_type;
        if (form.file_charge_value) body.file_charge_value = Number(form.file_charge_value);
      }
      if (form.note) body.note = form.note;
      await api.post(`/loan-requests/${pick.id}/decision`, body);
      setPick(null);
      load();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up" data-testid="loan-requests-page">
      <PageHeader
        title="Loan Requests"
        subtitle={canDecide ? "Review and approve loans against deposits requested by agents." : "Your loan-against-deposit requests submitted for customers."}
        action={canRaise ? (
          <Button onClick={() => setRaiseOpen(true)} data-testid="raise-loan-request-btn">
            <Plus size={16} weight="bold" /> New request
          </Button>
        ) : null}
      />

      <Card className="p-4 mb-4">
        <div className="flex gap-2 flex-wrap">
          {[
            ["requested", `Pending (${buckets.requested.length})`],
            ["approved", `Approved / Dispatched (${buckets.approved.length})`],
            ["rejected", `Rejected (${buckets.rejected.length})`],
          ].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`lr-tab-${v}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                tab === v ? "bg-emerald-700 text-white border-emerald-700"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}>
              {l}
            </button>
          ))}
        </div>
      </Card>

      <Card>
        {loading ? <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
         : list.length === 0 ? <EmptyState title="Nothing here yet" />
         : (
           <>
             {/* Mobile: card list */}
             <div className="md:hidden divide-y divide-slate-100">
               {list.map((r) => (
                 <div key={r.id} className="p-4" data-testid={`lr-row-${r.id}`}>
                   <div className="flex items-start justify-between gap-2 mb-2">
                     <div className="min-w-0">
                       <div className="font-medium text-slate-900 truncate">{r.customer_name}{!r.customer_id && r.applicant_info ? " · new" : ""}</div>
                       <div className="flex items-center gap-1.5 mt-0.5">
                         <Badge tone={r.request_type === "deposit" ? "info" : r.request_type === "commodity" ? "warning" : "neutral"}>
                           {reqTypeLabel(r.request_type)}
                         </Badge>
                         <div className="text-[11px] text-slate-500 truncate">{r.purpose || ""}</div>
                       </div>
                     </div>
                     <Badge tone={r.status === "dispatched" || r.status === "approved" ? "success"
                                 : r.status === "rejected" ? "danger" : "warning"}>
                       {r.status}
                     </Badge>
                   </div>
                   <div className="grid grid-cols-2 gap-2 text-[12px] text-slate-600">
                     <div>
                       <div className="text-[10px] uppercase text-slate-400">Amount</div>
                       <div className="mono font-semibold text-slate-900">{money(r.amount)}</div>
                     </div>
                     <div>
                       <div className="text-[10px] uppercase text-slate-400">{r.request_type === "deposit" ? "Against" : r.request_type === "commodity" ? "Commodity" : "Applicant"}</div>
                       <div className="mono truncate">
                         {r.request_type === "deposit"
                           ? `${(r.savings_product || "").toUpperCase()} · ${money(r.savings_balance || 0)}`
                           : r.request_type === "commodity"
                             ? (r.commodity_name || "—")
                             : (r.applicant_info?.phone || (r.customer_id ? "Registered" : "—"))}
                       </div>
                     </div>
                     <div>
                       <div className="text-[10px] uppercase text-slate-400">Tenure</div>
                       <div>{r.tenure_days} days{r.interest_rate ? ` · ${r.interest_rate}%` : ""}</div>
                     </div>
                     <div>
                       <div className="text-[10px] uppercase text-slate-400">Applied</div>
                       <div className="mono">{shortDate(r.requested_at)}</div>
                     </div>
                   </div>
                   {r.loan_id && (
                     <div className="text-[10px] mono text-slate-500 mt-2">→ Loan #{r.loan_id.slice(0,8)}</div>
                   )}
                   {canDecide && r.status === "requested" && (
                     <div className="flex gap-2 mt-3">
                       <button onClick={() => openDecide(r, "approved")}
                         className="flex-1 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-200 active:bg-emerald-100"
                         data-testid={`lr-approve-${r.id}`}>
                         <CheckCircle size={14} weight="bold" className="inline mr-1" />Approve
                       </button>
                       <button onClick={() => openDecide(r, "rejected")}
                         className="flex-1 py-2 rounded-lg bg-rose-50 text-rose-700 text-sm font-medium border border-rose-200 active:bg-rose-100"
                         data-testid={`lr-reject-${r.id}`}>
                         <XCircle size={14} weight="bold" className="inline mr-1" />Reject
                       </button>
                     </div>
                   )}
                 </div>
               ))}
             </div>

             {/* Desktop: table */}
             <div className="hidden md:block overflow-x-auto">
             <table className="w-full text-sm">
               <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                 <tr>
                   <th className="text-left px-4 py-3">Customer</th>
                   <th className="text-left px-4 py-3">Applied</th>
                   <th className="text-right px-4 py-3">Amount</th>
                   <th className="text-left px-4 py-3">Against</th>
                   <th className="text-left px-4 py-3">Tenure / Rate</th>
                   <th className="text-left px-4 py-3">Status</th>
                   <th />
                 </tr>
               </thead>
               <tbody>
                 {list.map((r) => (
                   <tr key={r.id} className="border-t border-slate-100" data-testid={`lr-row-${r.id}-desktop`}>
                     <td className="px-4 py-3">
                       <div className="font-medium text-slate-900">{r.customer_name}{!r.customer_id && r.applicant_info ? " · new" : ""}</div>
                       <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                         <Badge tone={r.request_type === "deposit" ? "info" : r.request_type === "commodity" ? "warning" : "neutral"}>
                           {reqTypeLabel(r.request_type)}
                         </Badge>
                         {r.purpose || ""}
                       </div>
                     </td>
                     <td className="px-4 py-3 mono">{shortDate(r.requested_at)}</td>
                     <td className="px-4 py-3 mono text-right font-medium">{money(r.amount)}</td>
                     <td className="px-4 py-3">
                       {r.request_type === "deposit" ? (
                         <>
                           <Badge tone="info">{(r.savings_product || "").toUpperCase()}</Badge>
                           <div className="text-xs text-slate-500 mt-0.5 mono">Bal {money(r.savings_balance || 0)}</div>
                         </>
                       ) : r.request_type === "commodity" ? (
                         <div className="text-xs text-slate-700">
                           <div className="font-medium">{r.commodity_name || "—"}</div>
                           {r.commodity_value ? <div className="mono text-slate-500">Val {money(r.commodity_value)}</div> : null}
                         </div>
                       ) : (
                         <div className="text-xs text-slate-500">{r.applicant_info?.phone || "Registered"}</div>
                       )}
                     </td>
                     <td className="px-4 py-3 text-slate-700 text-sm">
                       {r.tenure_days} days{r.interest_rate ? ` · ${r.interest_rate}% (${r.interest_method || "SI"})` : ""}
                     </td>
                     <td className="px-4 py-3">
                       <Badge tone={r.status === "dispatched" || r.status === "approved" ? "success"
                                  : r.status === "rejected" ? "danger" : "warning"}>
                         {r.status}
                       </Badge>
                       {r.loan_id && (
                         <div className="text-[10px] mono text-slate-500 mt-1">→ Loan #{r.loan_id.slice(0,8)}</div>
                       )}
                     </td>
                     <td className="px-4 py-3 text-right whitespace-nowrap">
                       {canDecide && r.status === "requested" && (
                         <>
                           <button onClick={() => openDecide(r, "approved")}
                             className="text-emerald-700 text-sm font-medium hover:underline mr-3"
                             data-testid={`lr-approve-${r.id}`}>
                             <CheckCircle size={14} weight="bold" className="inline mr-1" />Approve
                           </button>
                           <button onClick={() => openDecide(r, "rejected")}
                             className="text-rose-700 text-sm font-medium hover:underline"
                             data-testid={`lr-reject-${r.id}`}>
                             <XCircle size={14} weight="bold" className="inline mr-1" />Reject
                           </button>
                         </>
                       )}
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
             </div>
           </>
         )}
      </Card>

      <Modal open={!!pick} onClose={() => setPick(null)}
        title={decision === "approved" ? "Approve loan request" : "Reject loan request"}
        maxWidth="max-w-lg">
        {pick && (
          <form onSubmit={submitDecision} className="space-y-4">
            <div className="text-sm text-slate-600 space-y-0.5">
              <div><span className="text-slate-400">Type:</span> <Badge tone={pick.request_type === "deposit" ? "info" : pick.request_type === "commodity" ? "warning" : "neutral"}>{reqTypeLabel(pick.request_type)}</Badge></div>
              <div><span className="text-slate-400">Customer:</span> {pick.customer_name}{!pick.customer_id && pick.applicant_info ? " (new applicant)" : ""}</div>
              <div><span className="text-slate-400">Amount:</span> <span className="mono font-semibold text-emerald-700">{money(pick.amount)}</span></div>
              {pick.request_type === "deposit" && pick.savings_product && (
                <div><span className="text-slate-400">Against:</span> <span className="mono">{(pick.savings_product||"").toUpperCase()}</span> (bal {money(pick.savings_balance)})</div>
              )}
              {pick.request_type === "commodity" && pick.commodity_name && (
                <div><span className="text-slate-400">Commodity:</span> <span className="font-medium">{pick.commodity_name}</span>{pick.commodity_value ? <span className="mono text-slate-500"> · val {money(pick.commodity_value)}</span> : null}</div>
              )}
              <div><span className="text-slate-400">Tenure:</span> {pick.tenure_days} days</div>
              {pick.applicant_info && !pick.customer_id && (
                <div className="mt-1 text-xs bg-slate-50 border border-slate-200 rounded p-2 space-y-0.5">
                  <div>📞 {pick.applicant_info.phone}</div>
                  {pick.applicant_info.address && <div>🏠 {pick.applicant_info.address}</div>}
                  {pick.applicant_info.occupation && <div>💼 {pick.applicant_info.occupation}</div>}
                  {pick.applicant_info.reference && <div>👤 Ref: {pick.applicant_info.reference}</div>}
                </div>
              )}
              {pick.purpose && <div><span className="text-slate-400">Purpose:</span> {pick.purpose}</div>}
            </div>
            {decision === "approved" && (
              <>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
                  Set interest terms
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Interest rate %" type="number" step="0.01" required
                    testId="lr-rate"
                    value={form.interest_rate}
                    onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} />
                  <Select label="Interest method" testId="lr-method"
                    value={form.interest_method}
                    onChange={(e) => setForm({ ...form, interest_method: e.target.value })}>
                    <option value="SI">Simple Interest</option>
                    <option value="CI_yearly">Compound · Yearly</option>
                    <option value="CI_quarterly">Compound · Quarterly</option>
                    <option value="CI_monthly">Compound · Monthly</option>
                    <option value="CI_daily">Compound · Daily</option>
                  </Select>
                </div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
                  File charge (optional)
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Select label="Type" testId="lr-fc-type"
                    value={form.file_charge_type} onChange={(e) => setForm({ ...form, file_charge_type: e.target.value })}>
                    <option value="">— none —</option>
                    <option value="percent">Percent of principal</option>
                    <option value="flat">Flat rupees</option>
                  </Select>
                  <Input label="Value" type="number" step="0.01" testId="lr-fc-val"
                    value={form.file_charge_value}
                    onChange={(e) => setForm({ ...form, file_charge_value: e.target.value })}
                    disabled={!form.file_charge_type} />
                </div>
              </>
            )}
            <Input label="Note (optional)" testId="lr-note"
              value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            {err && <div className="text-sm text-rose-600">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" type="button" onClick={() => setPick(null)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="lr-confirm"
                className={decision === "rejected" ? "!bg-rose-600 hover:!bg-rose-700" : ""}>
                {saving ? "Saving…" : (decision === "approved" ? "Approve & Dispatch" : "Reject")}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <RaiseLoanRequestModal
        open={raiseOpen}
        onClose={() => setRaiseOpen(false)}
        onSubmitted={() => { setRaiseOpen(false); load(); }}
      />
    </div>
  );
}


function RaiseLoanRequestModal({ open, onClose, onSubmitted }) {
  const [requestType, setRequestType] = useState("deposit"); // deposit | general | commodity
  const [applicantMode, setApplicantMode] = useState("existing"); // existing | new (for general/commodity)
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [savings, setSavings] = useState([]);
  const [savingsId, setSavingsId] = useState("");
  const [form, setForm] = useState({ amount: "", tenure_days: "120", purpose: "", notes: "", commodity_name: "", commodity_value: "" });
  const [applicant, setApplicant] = useState({ name: "", phone: "", address: "", occupation: "", reference: "" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr(""); setDone(false);
    setRequestType("deposit"); setApplicantMode("existing");
    setCustomerId(""); setSavingsId(""); setSavings([]);
    setForm({ amount: "", tenure_days: "120", purpose: "", notes: "", commodity_name: "", commodity_value: "" });
    setApplicant({ name: "", phone: "", address: "", occupation: "", reference: "" });
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => setCustomers([]));
  }, [open]);

  useEffect(() => {
    if (!customerId || requestType !== "deposit") { setSavings([]); setSavingsId(""); return; }
    api.get(`/savings?customer_id=${customerId}`)
      .then(({ data }) => {
        // Show ALL active accounts (even 0-balance) so agent sees the real state
        const active = (data || []).filter((s) => s.status === "active");
        setSavings(active);
        const firstWithBalance = active.find((s) => (s.balance || 0) > 0);
        setSavingsId(firstWithBalance?.id || active[0]?.id || "");
      })
      .catch(() => setSavings([]));
  }, [customerId, requestType]);

  const savingsPick = savings.find((s) => s.id === savingsId);
  const maxAllowed = savingsPick ? (savingsPick.balance || 0) * 0.9 : 0;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const amt = Number(form.amount);
    const tenure = Number(form.tenure_days);
    if (!amt || amt <= 0) return setErr("Enter a valid amount");
    if (!tenure || tenure <= 0) return setErr("Enter a valid tenure");

    const body = {
      request_type: requestType,
      amount: amt,
      tenure_days: tenure,
      purpose: form.purpose || null,
      notes: form.notes || null,
    };

    if (requestType === "deposit") {
      if (!customerId) return setErr("Pick a customer");
      if (!savingsId) return setErr("Pick a deposit to secure the loan against");
      if ((savingsPick?.balance || 0) <= 0) return setErr("The selected deposit has no balance; cannot secure a loan against it");
      if (amt > maxAllowed) return setErr(`Amount exceeds 90% of deposit balance (${money(maxAllowed)})`);
      body.customer_id = customerId;
      body.savings_id = savingsId;
    } else {
      // general / commodity loan
      if (applicantMode === "existing") {
        if (!customerId) return setErr("Pick a customer");
        body.customer_id = customerId;
      } else {
        if (!applicant.name.trim() || !applicant.phone.trim()) return setErr("Applicant name and phone are required");
        body.applicant_info = {
          name: applicant.name.trim(),
          phone: applicant.phone.trim(),
          address: applicant.address || null,
          occupation: applicant.occupation || null,
          reference: applicant.reference || null,
        };
      }
      if (requestType === "commodity") {
        if (!form.commodity_name.trim()) return setErr("Commodity name is required");
        body.commodity_name = form.commodity_name.trim();
        if (form.commodity_value) body.commodity_value = Number(form.commodity_value);
      }
    }

    setSaving(true);
    try {
      await api.post("/loan-requests", body);
      setDone(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  const TypeTab = ({ v, label }) => (
    <button
      type="button"
      data-testid={`raise-loan-type-${v}`}
      onClick={() => setRequestType(v)}
      className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
        requestType === v
          ? "bg-emerald-700 text-white border-emerald-700"
          : "bg-white text-slate-700 border-slate-200"
      }`}
    >
      {label}
    </button>
  );

  return (
    <Modal open={open} onClose={onClose} title="Raise Loan Request" maxWidth="max-w-lg">
      {done ? (
        <div className="text-center py-6" data-testid="raise-loan-done">
          <div className="text-emerald-700 heading text-lg font-semibold mb-2">Request submitted ✓</div>
          <div className="text-sm text-slate-600 mb-4">
            Admin has been notified. Track status in this page.
          </div>
          <Button onClick={() => { onSubmitted(); }}>Done</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {/* Type toggle */}
          <div>
            <div className="text-xs font-medium text-slate-600 mb-1.5">Loan type</div>
            <div className="grid grid-cols-3 gap-2">
              <TypeTab v="deposit" label="Against deposit" />
              <TypeTab v="general" label="General" />
              <TypeTab v="commodity" label="Commodity" />
            </div>
          </div>

          {requestType === "deposit" && (
            <>
              <Select label="Customer" value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                testId="raise-loan-customer" required>
                <option value="">— pick a customer —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} · {c.customer_code || c.phone || ""}
                  </option>
                ))}
              </Select>

              {customerId && (
                savings.length === 0 ? (
                  <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    This customer has no active deposit. Open a deposit first, or switch to <b>General loan</b>.
                  </div>
                ) : (
                  <>
                    <Select label="Deposit account" value={savingsId}
                      onChange={(e) => setSavingsId(e.target.value)}
                      testId="raise-loan-savings" required>
                      {savings.map((s) => (
                        <option key={s.id} value={s.id}>
                          {(s.product || "").toUpperCase()} · {s.account_no || s.id.slice(0, 6)} · Bal {money(s.balance || 0)}
                        </option>
                      ))}
                    </Select>
                    {savingsPick && (savingsPick.balance || 0) <= 0 ? (
                      <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="raise-loan-zero-bal">
                        This deposit's current balance is <b>{money(0)}</b>. Post the deposit collection first, or pick another account, or switch to <b>General loan</b>.
                      </div>
                    ) : savingsPick && (
                      <div className="text-[11px] text-slate-500 -mt-2">
                        Max loan is 90% of balance: <span className="mono font-medium">{money(maxAllowed)}</span>
                      </div>
                    )}
                  </>
                )
              )}
            </>
          )}

          {requestType !== "deposit" && (
            <>
              <div>
                <div className="text-xs font-medium text-slate-600 mb-1.5">Applicant</div>
                <div className="flex gap-2">
                  <button type="button" data-testid="raise-loan-mode-existing"
                    onClick={() => setApplicantMode("existing")}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border ${
                      applicantMode === "existing" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-700 border-slate-200"
                    }`}>
                    Registered member
                  </button>
                  <button type="button" data-testid="raise-loan-mode-new"
                    onClick={() => setApplicantMode("new")}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border ${
                      applicantMode === "new" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-700 border-slate-200"
                    }`}>
                    New applicant
                  </button>
                </div>
              </div>

              {applicantMode === "existing" ? (
                <Select label="Customer" value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  testId="raise-loan-customer" required>
                  <option value="">— pick a customer —</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} · {c.customer_code || c.phone || ""}
                    </option>
                  ))}
                </Select>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Input label="Full name" required testId="raise-loan-app-name"
                      value={applicant.name}
                      onChange={(e) => setApplicant({ ...applicant, name: e.target.value })} />
                    <Input label="Phone" required testId="raise-loan-app-phone"
                      value={applicant.phone}
                      onChange={(e) => setApplicant({ ...applicant, phone: e.target.value })} />
                  </div>
                  <Input label="Address" testId="raise-loan-app-address"
                    value={applicant.address}
                    onChange={(e) => setApplicant({ ...applicant, address: e.target.value })} />
                  <div className="grid grid-cols-2 gap-3">
                    <Input label="Occupation" testId="raise-loan-app-occupation"
                      value={applicant.occupation}
                      onChange={(e) => setApplicant({ ...applicant, occupation: e.target.value })} />
                    <Input label="Reference" testId="raise-loan-app-reference"
                      value={applicant.reference}
                      onChange={(e) => setApplicant({ ...applicant, reference: e.target.value })} />
                  </div>
                </div>
              )}

              {requestType === "commodity" && (
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Commodity (e.g. Motorcycle)" required testId="raise-loan-commodity-name"
                    value={form.commodity_name}
                    onChange={(e) => setForm({ ...form, commodity_name: e.target.value })} />
                  <Input label="Commodity value ₹" type="number" step="0.01" testId="raise-loan-commodity-value"
                    value={form.commodity_value}
                    onChange={(e) => setForm({ ...form, commodity_value: e.target.value })} />
                </div>
              )}
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Input label="Amount" type="number" step="0.01" required
              testId="raise-loan-amount"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            <Input label="Tenure (days)" type="number" required
              testId="raise-loan-tenure"
              value={form.tenure_days}
              onChange={(e) => setForm({ ...form, tenure_days: e.target.value })} />
          </div>
          <Input label="Purpose (short)" testId="raise-loan-purpose"
            value={form.purpose}
            onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
          <Input label="Notes (optional)" testId="raise-loan-notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />

          <div className="text-[11px] text-slate-500">
            Interest rate & method will be set by admin at approval time.
          </div>

          {err && <div className="text-sm text-rose-600" data-testid="raise-loan-err">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="raise-loan-submit">
              {saving ? "Submitting…" : "Submit request"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
